import React, { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { DebugTrace } from "./DebugTrace";
import Highcharts from "highcharts";
import Heatmap from "highcharts/modules/heatmap";
import HighchartsReact from "highcharts-react-official";
import {
  buildHeatmapData,
  buildMultiAxis,
  buildMultiSeriesLine,
  buildPercentageChart,
  buildSingleSeries,
  buildStacked,
  buildStackedPercent,
} from "@/viz/chart-data";
import { SignalIcon } from "@/components/icons/SignalIcon";

// Idempotent — safe to call multiple times across modules
if (typeof Highcharts === "object" && typeof window !== "undefined") {
  Heatmap(Highcharts);
}

const ChartRenderer = ({ config }: { config: any }) => {
  const options = React.useMemo(() => {
    try {
      if (!config || !config.mode) return null;
      const { mode } = config;

      // Normalize: AI always outputs "labels"; some builders (stacked, stacked_percent)
      // read "multi_series_labels" for x-axis categories. Cross-populate so both work.
      const normalized = {
        ...config,
        multi_series: config.multi_series ?? [],
        labels: config.labels ?? config.multi_series_labels ?? [],
        multi_series_labels: config.multi_series_labels ?? config.labels ?? [],
      };

      switch (mode) {
        case "line":
          return buildMultiSeriesLine({
            width: undefined,
            height: 300,
            data: normalized,
            yAxisTitle: normalized.values_name,
            xAxisTitle: normalized.labels_name,
          });
        case "multi_axis":
          return buildMultiAxis({
            width: undefined,
            height: 300,
            data: normalized,
          });
        case "stacked":
          return buildStacked({
            width: undefined,
            height: 300,
            data: normalized,
            totalRequired: normalized.total_required,
          });
        case "percent":
        case "stacked_percent":
          return buildStackedPercent({
            width: undefined,
            height: 300,
            data: normalized,
          });
        case "pie":
          return buildPercentageChart({
            width: undefined,
            height: 300,
            data: normalized,
          });
        case "heatmap":
          return buildHeatmapData({
            width: undefined,
            height: 300,
            data: normalized,
          });
        default:
          return buildSingleSeries({
            width: undefined,
            height: 300,
            data: normalized,
            yAxisTitle: normalized.values_name,
            xAxisTitle: normalized.labels_name,
          });
      }
    } catch (e) {
      console.error("Chart build error", e);
      return null;
    }
  }, [config]);

  if (!options) return null;

  return (
    <div className="w-full my-4 rounded-xl overflow-hidden border border-border/50 bg-card/50 shadow-sm">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// ThinkingIndicator
// Cycles through status phrases while the assistant is generating a response.
//
// Performance notes:
//  - Only mounts when isLoading=true; React unmounts it the moment the first
//    chunk arrives, so the interval never runs during normal message display.
//  - useEffect cleanup (return () => clearInterval) guarantees the timer is
//    cancelled on unmount — no memory leaks or stale state updates.
//  - Two tiny state booleans (index + visible) — negligible overhead.
// ---------------------------------------------------------------------------

const THINKING_PHRASES = [
  "Thinking", // 1. deciding what to do
  "Fetching records", // 2. calling data tools
  "Checking flights", // 3. specific tool lookup
  "Analyzing data", // 4. processing what came back
  "Crunching numbers", // 5. computing summaries
  "Reasoning", // 6. forming the answer
  "Processing", // 7. finalizing response
];

function ThinkingIndicator() {
  const [index, setIndex] = useState(0);
  // Controls the CSS opacity for a smooth fade-out → swap text → fade-in effect.
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Use a local counter so we can read the current index inside the closure
    // without adding it to the dep array (which would keep re-registering).
    let currentIndex = 0;

    // Every 2.5s: fade text out (300ms), bump phrase, fade back in.
    // Stops once the last phrase ("Processing") is reached — avoids
    // looping back to "Thinking" if the AI takes longer than the full
    // cycle (~17.5s total).
    const interval = setInterval(() => {
      if (currentIndex >= THINKING_PHRASES.length - 1) return; // hold on last
      setVisible(false);
      setTimeout(() => {
        currentIndex += 1;
        setIndex(currentIndex);
        setVisible(true);
      }, 300); // 300ms matches the CSS transition-duration on opacity
    }, 2500);

    // Cleanup: cancel interval when component unmounts (i.e. when AI responds).
    return () => clearInterval(interval);
  }, []); // Empty deps — only register once on mount.

  return (
    <div className="flex items-center gap-2.5 h-5 px-1">
      {/* Animated phrase text — fades between entries */}
      <span
        className={cn(
          "text-xs font-medium text-muted-foreground transition-opacity duration-300",
          visible ? "opacity-100" : "opacity-0"
        )}
      >
        {THINKING_PHRASES[index]}
      </span>
      {/* Classic bouncing dots alongside the phrase */}
      <div className="flex items-center gap-1">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.3s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.15s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60" />
      </div>
    </div>
  );
}

// Secondary defence: catches any leaked raw tool-call syntax that slips past
// the backend guard (e.g. replayed from DB before the fix was deployed).
const RAW_TOOL_CALL_PATTERN = /to=functions\.\w+/i;
function looksLikeRawToolCall(text: string): boolean {
  return RAW_TOOL_CALL_PATTERN.test(text);
}
const GRACEFUL_TOOL_ERROR =
  "I wasn't able to generate a clean response for that query. Please try asking again — it usually resolves on the second attempt.";

interface ChatMessageProps {
  role: "user" | "assistant" | "system" | "function";
  content: string;
  isLoading?: boolean;
  debugTrace?: Array<{
    function_name: string;
    trace_type?: string;
    summary_key?: string;
    cache_hit?: boolean;
    precompute_metadata?: Record<string, any>;
    precompute_payload?: Record<string, any>;
    fallback_used?: string;
    tool_params?: Record<string, any>;
    data_provenance?: Record<string, any>;
  }>;
}

/**
 * Simple markdown-like formatting for chat messages.
 * Handles basic formatting without external dependencies.
 */
function parseTextContent(content: string): React.ReactNode {
  const lines = content.split("\n");

  const formatInlineBold = (text: string) => {
    if (!text.includes("**") && !text.includes("*") && !text.includes("["))
      return text;

    // First handle links
    let parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);

    return parts.map((part, j) => {
      // Markdown link [label](url)
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const label = linkMatch[1];
        const href = linkMatch[2];
        const isInternal = href.startsWith("/");
        return isInternal ? (
          <Link
            key={j}
            to={href}
            className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
          >
            {label}
          </Link>
        ) : (
          <a
            key={j}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
          >
            {label}
          </a>
        );
      }

      // If not a link, handle bold and italic
      // Split on **bold** and *italic* tokens
      const textParts = part.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
      return textParts.map((textPart, k) => {
        // Bold (**text**)
        if (textPart.startsWith("**") && textPart.endsWith("**")) {
          return (
            <strong key={`${j}-${k}`} className="font-semibold">
              {textPart.slice(2, -2)}
            </strong>
          );
        }
        // Italic (*text*)
        if (
          textPart.startsWith("*") &&
          textPart.endsWith("*") &&
          textPart.length > 2
        ) {
          return (
            <em key={`${j}-${k}`} className="italic text-muted-foreground">
              {textPart.slice(1, -1)}
            </em>
          );
        }
        return textPart;
      });
    });
  };

  const splitTableLine = (line: string) => {
    const raw = line.split("|").map((c) => c.trim());
    if (raw.length < 3) return null;
    if (raw[0] === "") raw.shift();
    if (raw[raw.length - 1] === "") raw.pop();
    return raw;
  };

  const isTableSeparator = (line: string) =>
    /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      // Skip empty lines (they'll be handled by spacing between elements)
      i += 1;
      continue;
    }

    // Table block (markdown pipe table)
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const headerCells = splitTableLine(line) || [];
      i += 2; // skip header + separator

      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes("|")) {
        const rowCells = splitTableLine(lines[i]);
        if (rowCells && rowCells.length > 0) {
          rows.push(rowCells);
        }
        i += 1;
      }

      const maxCols = Math.max(
        headerCells.length,
        ...rows.map((r) => r.length)
      );

      elements.push(
        <div key={`table-${i}`} className="my-2 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                {Array.from({ length: maxCols }).map((_, idx) => (
                  <th key={idx} className="px-2 py-1 text-left font-semibold">
                    {formatInlineBold(headerCells[idx] || "")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={rowIdx} className="border-b border-border/60">
                  {Array.from({ length: maxCols }).map((_, idx) => (
                    <td key={idx} className="px-2 py-1">
                      {formatInlineBold(row[idx] || "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Headers (support up to 6 levels of headers)
    const headerMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const text = headerMatch[2];
      const className =
        level === 1
          ? "text-lg font-bold mt-4 mb-2"
          : level === 2
            ? "text-base font-semibold mt-3 mb-2"
            : level === 3
              ? "text-sm font-semibold mt-2 mb-1"
              : "text-sm font-medium mt-2 mb-1 text-muted-foreground"; // h4, h5, h6
      elements.push(
        <div key={`h-${i}`} className={className}>
          {formatInlineBold(text)}
        </div>
      );
      i += 1;
      continue;
    }

    // List items (- or numbered) - collect consecutive items into a list block
    const listMatch =
      trimmed.match(/^[-•]\s+(.+)$/) || trimmed.match(/^\d+\.\s+(.+)$/);
    if (listMatch) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length) {
        const currentLine = lines[i];
        const currentTrimmed = currentLine.trim();
        const currentMatch =
          currentTrimmed.match(/^[-•]\s+(.+)$/) ||
          currentTrimmed.match(/^\d+\.\s+(.+)$/);
        if (!currentMatch || !currentTrimmed) break;

        const text = currentMatch[1];
        const formattedText = formatInlineBold(text);
        listItems.push(
          <li key={`li-${i}`} className="mb-1">
            {formattedText}
          </li>
        );
        i += 1;
      }
      // Wrap list items in <ul> with proper spacing
      elements.push(
        <ul key={`ul-${i}`} className="ml-4 my-3 list-disc list-outside">
          {listItems}
        </ul>
      );
      continue;
    }

    // Regular line
    elements.push(
      <p key={`p-${i}`} className="mb-2">
        {formatInlineBold(line)}
      </p>
    );
    i += 1;
  }

  return elements;
}

// Experimental: AI code breaking. Chart rendering is under active development and not yet stable for AI assistant.
// Set to true once rendering pipeline is validated end-to-end.
const CHARTS_ENABLED = false;
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Top-level formatter: splits content on ```json:chart blocks and renders
 * each segment as either a Highcharts chart or formatted markdown text.
 *
 * Streaming safety: the model emits chart JSON token-by-token, so during
 * streaming the closing fence hasn't arrived yet.  Both paths handle this:
 *
 * CHARTS_ENABLED=false — strip complete blocks AND truncate any in-progress
 *   opening fence so raw partial JSON is never visible.
 *
 * CHARTS_ENABLED=true  — render complete blocks normally; for an in-progress
 *   block, render only the text that precedes the opening fence and hold the
 *   partial JSON back until the closing fence arrives (no layout jump, no
 *   raw JSON flash).
 */
function formatMessage(content: string): React.ReactNode {
  const OPEN_FENCE = "```json:chart";
  const COMPLETE_BLOCK_RE = /```json:chart\n[\s\S]*?\n```/g;

  if (!CHARTS_ENABLED) {
    // 1. Remove any fully-closed chart blocks.
    // 2. Truncate everything from an unclosed opening fence to end-of-string
    //    so mid-stream partial JSON is never shown to the user.
    const stripped = content
      .replace(COMPLETE_BLOCK_RE, "")
      .replace(new RegExp(`${OPEN_FENCE}[\\s\\S]*$`), "")
      .trim();
    return parseTextContent(stripped);
  }

  // ── CHARTS_ENABLED = true path ────────────────────────────────────────────

  // Detect an in-progress (unclosed) chart block mid-stream:
  // the opening fence exists but the closing fence has not yet arrived.
  const openIdx = content.lastIndexOf(OPEN_FENCE);
  const hasOpenFence = openIdx !== -1;
  const closingFenceAfterOpen =
    hasOpenFence && /\n```/.test(content.slice(openIdx + OPEN_FENCE.length));
  const chartInProgress = hasOpenFence && !closingFenceAfterOpen;

  if (chartInProgress) {
    // Render only the text before the opening fence while the JSON streams in.
    // Once the closing fence arrives on a future chunk, the full block renders.
    const safeContent = content.slice(0, openIdx).trim();
    return parseTextContent(safeContent);
  }

  // Regex matches fenced blocks of the form: ```json:chart\n{...}\n```
  const chartBlockRegex = /```json:chart\n([\s\S]*?)\n```/g;
  const segments: Array<{ type: "text" | "chart"; raw: string }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = chartBlockRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        raw: content.slice(lastIndex, match.index),
      });
    }
    segments.push({ type: "chart", raw: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    segments.push({ type: "text", raw: content.slice(lastIndex) });
  }

  if (segments.length === 0) {
    return parseTextContent(content);
  }

  return (
    <>
      {segments.map((seg, idx) => {
        if (seg.type === "chart") {
          try {
            const config = JSON.parse(seg.raw);
            return <ChartRenderer key={`chart-${idx}`} config={config} />;
          } catch {
            return null;
          }
        }
        return (
          <React.Fragment key={`text-${idx}`}>
            {parseTextContent(seg.raw)}
          </React.Fragment>
        );
      })}
    </>
  );
}

export function ChatMessage({
  role,
  content,
  isLoading,
  debugTrace,
}: ChatMessageProps) {
  const isUser = role === "user";
  const displayContent =
    !isUser && !isLoading && looksLikeRawToolCall(content)
      ? GRACEFUL_TOOL_ERROR
      : content;

  return (
    <div
      className={cn(
        "flex gap-4 p-4 w-full animate-in fade-in slide-in-from-bottom-2 duration-300",
        isUser ? "flex-row-reverse" : "flex-row"
      )}
    >
      {/* Avatar - Only show for assistant to keep user side clean and modern */}
      {!isUser && (
        <div className="mt-1 flex h-8 w-8 shrink-0 select-none items-center justify-center overflow-hidden rounded-full ring-1 ring-primary/20 shadow-sm">
          <SignalIcon className="h-full w-full" />
        </div>
      )}

      {/* Message content */}
      <div
        className={cn(
          "flex max-w-[85%] flex-col gap-1.5",
          isUser ? "items-end" : "items-start"
        )}
      >
        <div
          className={cn(
            "px-5 py-3.5 text-[15px] leading-relaxed shadow-sm transition-all break-words",
            isUser
              ? "bg-secondary text-foreground rounded-2xl rounded-br-sm font-medium"
              : "bg-card border border-border/50 text-card-foreground rounded-2xl rounded-bl-sm"
          )}
        >
          {isLoading ? (
            <ThinkingIndicator />
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none prose-p:leading-relaxed prose-pre:bg-muted/50 prose-pre:border prose-pre:border-border/50">
              {formatMessage(displayContent)}
            </div>
          )}
        </div>

        {/* Debug trace - internal users only */}
        {debugTrace && debugTrace.length > 0 && !isUser && (
          <div className="mt-1">
            <DebugTrace traces={debugTrace} />
          </div>
        )}
      </div>
    </div>
  );
}
